/* Users admin panel — extracted from Admin.jsx tech-debt split.
 *
 * Capabilities by role:
 *   • SuperAdmin: change role, grant/revoke lifetime, freeze, soft-delete, restore.
 *   • Admin: freeze, soft-delete, restore (no role/tier changes).
 *
 * Sortable by name, email, joined-date and recipe count (oldest/newest etc.).
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import UserNamesDialog from "@/pages/admin/UserNamesDialog";

const SORTS = {
  name_az:        { label: "Name (A → Z)",          fn: (a, b) => (a.name || "").localeCompare(b.name || "") },
  name_za:        { label: "Name (Z → A)",          fn: (a, b) => (b.name || "").localeCompare(a.name || "") },
  email_az:       { label: "Email (A → Z)",         fn: (a, b) => (a.email || "").localeCompare(b.email || "") },
  joined_newest:  { label: "Joined — newest first", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  joined_oldest:  { label: "Joined — oldest first", fn: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") },
  recipes_most:   { label: "Most recipes first",    fn: (a, b) => (b.recipe_count || 0) - (a.recipe_count || 0) },
  recipes_least:  { label: "Fewest recipes first",  fn: (a, b) => (a.recipe_count || 0) - (b.recipe_count || 0) },
};

const pastMatch = (u, q) => {
  const t = q.trim().toLowerCase();
  return t ? (u.past_names || []).find((n) => n.toLowerCase().includes(t)) : null;
};

const matchUser = (u, q) => {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [u.email, u.name, u.username].some((v) => (v || "").toLowerCase().includes(t)) || !!pastMatch(u, q);
};

export default function UsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sortKey, setSortKey] = useState("joined_newest");
  const isSuper = user?.role === "SuperAdmin";
  const [namesFor, setNamesFor] = useState(null);
  const [search, setSearch] = useState("");

  const reload = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { reload(); }, []);

  const sortedUsers = useMemo(() => {
    const fn = (SORTS[sortKey] || SORTS.joined_newest).fn;
    return [...users].sort(fn);
  }, [users, sortKey]);

  const promote = async (u, role) => {
    await api.patch(`/admin/users/${u.user_id}`, { role });
    toast.success(`${u.email} → ${role}`);
    reload();
  };

  const grantTier = async (u, tier) => {
    const label = tier === "lifetime" ? "lifetime privileges (free forever)" : tier;
    if (!window.confirm(`Set ${u.email} to ${label}?`)) return;
    await api.patch(`/admin/users/${u.user_id}`, { subscription_tier: tier });
    toast.success(`${u.email} → ${label}`);
    reload();
  };

  const toggleFreeze = async (u) => {
    const willFreeze = !u.frozen;
    const reason = willFreeze ? (window.prompt(`Freeze ${u.email}? They will no longer be able to create new recipes or cookbooks. Reason (optional, shown to the user):`) ?? null) : null;
    if (willFreeze && reason === null) return; // cancelled
    try {
      await api.post(`/admin/users/${u.user_id}/freeze`, { frozen: willFreeze, reason: reason || "" });
      toast.success(willFreeze ? `${u.email} frozen` : `${u.email} unfrozen`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update freeze state");
    }
  };

  const softDelete = async (u) => {
    if (!window.confirm(`Soft-delete ${u.email}?\n\n• Account is immediately disabled — they cannot log in\n• Their cookbooks and recipes remain hidden but recoverable for 30 days\n• After 30 days everything is permanently destroyed (background cleanup)\n\nClick OK to proceed.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`${u.email} soft-deleted (30-day rescue window)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const restore = async (u) => {
    if (!window.confirm(`Restore ${u.email}? They will be able to log in again and the freeze flag will also be cleared.`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/restore`);
      toast.success(`${u.email} restored`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const editUsername = async (u) => {
    const current = u.username || "";
    const next = window.prompt(
      `Edit @username for ${u.email}\n\nRules: 3-20 characters, letters/numbers/underscore only, unique.\nLeave BLANK to clear the username entirely (user will show as "Anonymous Cook").`,
      current
    );
    if (next === null) return;
    try {
      await api.post(`/admin/users/${u.user_id}/username`, { username: next.trim() });
      toast.success(next.trim() ? `Username updated to @${next.trim()}` : "Username cleared");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const toggleUsernameSuspend = async (u) => {
    const willSuspend = !u.username_suspended;
    const reason = willSuspend ? (window.prompt(`Suspend @${u.username || "(no username)"} for ${u.email}?\n\nThe user will show as "Anonymous Cook" publicly and can't self-update their handle until you unsuspend them.\n\nReason (audit trail, optional):`) ?? null) : null;
    if (willSuspend && reason === null) return;
    try {
      await api.post(`/admin/users/${u.user_id}/username`, { suspended: willSuspend, reason: reason || "" });
      toast.success(willSuspend ? "Username suspended" : "Username unsuspended");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle suspension");
    }
  };

  return (
    <div className="card-recipe overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs text-rf-ink-soft">
          {isSuper
            ? <>As a <b>SuperAdmin</b> you can promote users, grant Lifetime tier, freeze accounts and soft-delete.</>
            : <>As an <b>Admin</b> you can freeze accounts and soft-delete users (30-day rescue window). Role changes are SuperAdmin-only.</>}
        </p>
        <input
          data-testid="admin-users-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email, name, username or any past name…"
          className="input-rf !py-2 text-sm max-w-xs"
        />
        <div className="flex items-center gap-2 text-xs">
          <label htmlFor="user-sort" className="text-rf-ink-soft font-bold uppercase tracking-wider">Sort:</label>
          <select
            id="user-sort"
            data-testid="admin-users-sort"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="border border-rf-ink/15 rounded-lg px-3 py-1.5 bg-white font-medium"
          >
            {Object.entries(SORTS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-rf-ink-soft border-b border-rf-ink/10"><th className="py-2">Email</th><th>Name</th><th>Username</th><th>Role</th><th>Tier</th><th>Recipes</th><th>Joined</th><th>Manage</th></tr>
        </thead>
        <tbody>
          {sortedUsers.filter((u) => matchUser(u, search)).map((u) => (
            <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className={`border-b border-rf-ink/5 ${u.deleted_at ? "opacity-50" : ""}`}>
              <td className="py-3 font-mono text-xs">
                {u.email}
                {u.deleted_at && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-red-700">deleted</span>}
                {!u.deleted_at && u.frozen && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700">frozen</span>}
              </td>
              <td>
                {u.name}
                {pastMatch(u, search) && (
                  <span className="block text-[10px] text-rf-orange font-bold" data-testid={`admin-user-past-match-${u.user_id}`}>was: {pastMatch(u, search)}</span>
                )}
              </td>
              <td data-testid={`admin-user-username-${u.user_id}`} className="text-xs">
                {u.username ? (
                  <span className={`font-mono ${u.username_suspended ? "line-through text-red-600" : "text-rf-ink"}`}>@{u.username}</span>
                ) : (
                  <span className="text-rf-ink-soft italic">—</span>
                )}
                {u.username_suspended && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-red-700">suspended</span>
                )}
              </td>
              <td><span className={`chip ${u.role === "SuperAdmin" ? "!bg-rf-orange !text-white" : u.role === "Admin" ? "!bg-rf-amber !text-rf-ink" : ""}`}>{u.role}</span></td>
              <td>
                {u.subscription_tier === "lifetime"
                  ? <span className="chip !bg-emerald-500/20 !text-emerald-800 font-bold">★ Lifetime</span>
                  : <span className="text-rf-ink-soft">{u.subscription_tier}</span>}
              </td>
              <td>{u.recipe_count}</td>
              <td className="text-xs text-rf-ink-soft whitespace-nowrap">
                {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </td>
              <td>
                <div className="flex flex-col gap-1.5">
                  {/* SuperAdmin-only: role + lifetime */}
                  {isSuper && (
                    <>
                      <select
                        data-testid={`admin-user-role-${u.user_id}`}
                        value=""
                        onChange={(e) => e.target.value && promote(u, e.target.value)}
                        className="text-xs border border-rf-ink/10 rounded-lg px-2 py-1"
                      >
                        <option value="">Change role…</option>
                        <option value="Free_Tier">Free_Tier</option>
                        <option value="Paid_Subscriber">Paid_Subscriber</option>
                        <option value="Admin">Admin</option>
                        <option value="SuperAdmin">SuperAdmin</option>
                      </select>
                      {u.subscription_tier !== "lifetime" ? (
                        <button
                          data-testid={`admin-grant-lifetime-${u.user_id}`}
                          onClick={() => grantTier(u, "lifetime")}
                          className="text-xs font-bold px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/30 transition-colors"
                        >
                          ★ Grant lifetime
                        </button>
                      ) : (
                        <button
                          data-testid={`admin-revoke-lifetime-${u.user_id}`}
                          onClick={() => grantTier(u, "free")}
                          className="text-xs px-2 py-1 rounded-lg bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30 transition-colors"
                        >
                          Revoke → Free
                        </button>
                      )}
                    </>
                  )}
                  {/* Available to both Admin + SuperAdmin: freeze + delete + restore */}
                  {u.role !== "SuperAdmin" && !u.deleted_at && (
                    <button
                      data-testid={`admin-edit-username-${u.user_id}`}
                      onClick={() => editUsername(u)}
                      className="text-xs px-2 py-1 rounded-lg bg-rf-cream-alt text-rf-ink hover:bg-rf-amber/30 transition-colors"
                    >
                      ✎ Edit @username
                    </button>
                  )}
                  {!u.deleted_at && (
                    <button
                      data-testid={`admin-user-names-${u.user_id}`}
                      onClick={() => setNamesFor(u)}
                      className="text-xs px-2 py-1 rounded-lg bg-rf-cream-alt text-rf-ink hover:bg-rf-amber/30 transition-colors"
                    >
                      ✎ Recipe names
                    </button>
                  )}
                  {u.role !== "SuperAdmin" && !u.deleted_at && u.username && (
                    <button
                      data-testid={`admin-suspend-username-${u.user_id}`}
                      onClick={() => toggleUsernameSuspend(u)}
                      className={`text-xs px-2 py-1 rounded-lg transition-colors ${u.username_suspended ? "bg-emerald-500/20 text-emerald-900 hover:bg-emerald-500/40" : "bg-red-500/10 text-red-800 hover:bg-red-500/30"}`}
                    >
                      {u.username_suspended ? "Unsuspend username" : "Suspend username"}
                    </button>
                  )}
                  {u.role !== "SuperAdmin" && !u.deleted_at && (
                    <button
                      data-testid={`admin-freeze-${u.user_id}`}
                      onClick={() => toggleFreeze(u)}
                      className={`text-xs px-2 py-1 rounded-lg transition-colors ${u.frozen ? "bg-amber-500/30 text-amber-900 hover:bg-amber-500/50" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-amber-500/20"}`}
                    >
                      {u.frozen ? "❄ Unfreeze" : "Freeze account"}
                    </button>
                  )}
                  {u.role !== "SuperAdmin" && !u.deleted_at && (
                    <button
                      data-testid={`admin-soft-delete-${u.user_id}`}
                      onClick={() => softDelete(u)}
                      className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-800 hover:bg-red-500/30 transition-colors font-bold"
                    >
                      🗑 Delete (30d rescue)
                    </button>
                  )}
                  {u.deleted_at && (
                    <button
                      data-testid={`admin-restore-${u.user_id}`}
                      onClick={() => restore(u)}
                      className="text-xs font-bold px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-900 hover:bg-emerald-500/40 transition-colors"
                    >
                      ↺ Restore
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <UserNamesDialog target={namesFor} canEditUsername={isSuper} onClose={() => setNamesFor(null)} onChanged={reload} />
    </div>
  );
}
